import { ChangeEvent, useRef } from "react";
import type { ThemeFieldPreview } from "containers/theme-setter/types";
import { FieldInfoButton } from "containers/theme-setter/components/FieldInfoButton";

type FieldInputProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onUpload?: (file: File) => void;
  onClearUpload?: () => void;
  uploading?: boolean;
  className?: string;
  labelClassName?: string;
  uppercaseLabel?: boolean;
  preview?: ThemeFieldPreview;
  pendingUploadPreviewUrl?: string;
  pendingUploadFileName?: string;
};

export const FieldInput = ({
  label,
  value,
  onChange,
  onUpload,
  onClearUpload,
  uploading,
  className = "",
  labelClassName = "",
  uppercaseLabel,
  preview,
  pendingUploadPreviewUrl,
  pendingUploadFileName,
}: FieldInputProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleUploadClick = () => {
    if (onUpload && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!onUpload) return;
    const file = event.target.files?.[0];
    if (!file) return;
    onUpload(file);
    event.target.value = "";
  };
  const hasPendingUpload = Boolean(
    pendingUploadPreviewUrl || pendingUploadFileName
  );

  return (
    <div className={`flex flex-col gap-2 text-sm ${className}`}>
      <div className="flex items-center gap-1.5">
        <span
          className={`text-gray-300 ${
            uppercaseLabel ? "tracking-wide" : ""
          } ${labelClassName}`.trim()}
        >
          {label}
        </span>
        {preview && <FieldInfoButton label={label} preview={preview} />}
      </div>
      <div className={`flex gap-2 ${onUpload ? "items-start" : ""}`}>
        <input
          type="text"
          className="rounded-md border border-border bg-gray-900 px-3 py-2 text-sm focus:border-primary focus:outline-none flex-1 truncate"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        {onUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg,.gif,.webp,.avif,.ico"
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              type="button"
              className="shrink-0 rounded-md border border-border bg-gray-800 px-3 py-2 text-xs uppercase tracking-wide hover:bg-gray-800/80 disabled:opacity-50"
              onClick={handleUploadClick}
              disabled={uploading}
            >
              {uploading ? "Processing..." : "Select file"}
            </button>
          </>
        )}
      </div>
      {hasPendingUpload && (
        <div className="flex gap-3 rounded-md border border-border bg-gray-800/60 p-3">
          {pendingUploadPreviewUrl ? (
            <div className="h-16 w-16 overflow-hidden rounded border border-border bg-gray-900">
              <img
                src={pendingUploadPreviewUrl}
                alt={`${label} preview`}
                className="h-full w-full object-contain"
              />
            </div>
          ) : (
            <div className="h-16 w-16 rounded border border-border bg-gray-900 flex items-center justify-center text-xs text-gray-500">
              No preview
            </div>
          )}
          <div className="flex-1 text-xs text-gray-300 space-y-1">
            {pendingUploadFileName && (
              <p className="font-semibold text-gray-100">
                {pendingUploadFileName}
              </p>
            )}
            <p>Previewing locally. The image will upload when you click Save.</p>
            {onClearUpload && (
              <button
                type="button"
                className="text-left text-primary hover:text-primary/80"
                onClick={onClearUpload}
              >
                Remove selection
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
